import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PlanSettingsPanel = ({ plan, onUpdate, clients, onSave, isSaving }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(v => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Settings" size={14} className="text-primary" />
          </div>
          <span className="text-sm font-semibold text-foreground">Plan Settings</span>
        </div>
        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
      </button>
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="px-4 pb-4 space-y-3 border-t border-border pt-3"
        >
          {/* Plan Name */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Plan Name</label>
            <Input
              placeholder="e.g. Fat Loss – 1800 kcal"
              value={plan?.name || ''}
              onChange={e => onUpdate({ name: e?.target?.value })}
            />
          </div>

          {/* Client */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Assign to Client</label>
            <select
              value={plan?.clientId || ''}
              onChange={e => onUpdate({ clientId: e?.target?.value })}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
            >
              <option value="">Select client...</option>
              {clients?.map(c => (
                <option key={c?.id} value={c?.id}>{c?.full_name || c?.email}</option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Start Date</label>
              <Input
                type="date"
                value={plan?.startDate || ''}
                onChange={e => onUpdate({ startDate: e?.target?.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">End Date</label>
              <Input
                type="date"
                value={plan?.endDate || ''}
                onChange={e => onUpdate({ endDate: e?.target?.value })}
              />
            </div>
          </div>

          {/* Macro Targets */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-2">Daily Macro Targets</label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-primary w-20">Calories</span>
                <Input
                  type="number"
                  placeholder="2000"
                  value={plan?.targetCalories || ''}
                  onChange={e => onUpdate({ targetCalories: parseFloat(e?.target?.value) || 0 })}
                  className="flex-1"
                />
                <span className="text-xs text-muted-foreground">kcal</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-blue-400 w-20">Protein</span>
                <Input
                  type="number"
                  placeholder="150"
                  value={plan?.targetProtein || ''}
                  onChange={e => onUpdate({ targetProtein: parseFloat(e?.target?.value) || 0 })}
                  className="flex-1"
                />
                <span className="text-xs text-muted-foreground">g</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-400 w-20">Carbs</span>
                <Input
                  type="number"
                  placeholder="200"
                  value={plan?.targetCarbs || ''}
                  onChange={e => onUpdate({ targetCarbs: parseFloat(e?.target?.value) || 0 })}
                  className="flex-1"
                />
                <span className="text-xs text-muted-foreground">g</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-orange-400 w-20">Fats</span>
                <Input
                  type="number"
                  placeholder="65"
                  value={plan?.targetFats || ''}
                  onChange={e => onUpdate({ targetFats: parseFloat(e?.target?.value) || 0 })}
                  className="flex-1"
                />
                <span className="text-xs text-muted-foreground">g</span>
              </div>
            </div>
          </div>

          <Button
            onClick={onSave}
            disabled={isSaving || !plan?.name || !plan?.clientId}
            className="w-full"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={14} className="mr-2" />
                Save Plan to Client
              </>
            )}
          </Button>
        </motion.div>
      )}
    </div>
  );
};

export default PlanSettingsPanel;
